import { SystemStyleObject, Theme } from '@mui/system';

/**
 * Zentrales PVTA-Design-Modul (Single Source of Truth).
 *
 * Diese sx-Bausteine waren zuvor in vielen Seiten inline dupliziert
 * (cardSx, fieldSx, iconCircleSx, primaryButtonSx, ...). Werte sind 1:1
 * aus den vorhandenen Definitionen übernommen, damit das Umstellen der
 * fertigen Seiten keine sichtbare Regression erzeugt.
 *
 * Typisiert als SystemStyleObject (einzelner, spreadbarer Objekttyp), damit
 * `{ ...cardSx, ... }` an den Aufrufstellen weiterhin funktioniert.
 * Farben/Radien/Schatten kommen aus den CSS-Tokens in index.css.
 */

type Sx = SystemStyleObject<Theme>;

export const GREEN_950 = 'var(--pvta-green-950)';
export const GREEN_900 = 'var(--pvta-green-900)';
export const GREEN_800 = 'var(--pvta-green-800)';
export const GREEN_700 = 'var(--pvta-green-700)';
export const GREEN_600 = 'var(--pvta-green-600)';
export const GOLD = 'var(--pvta-gold)';
export const GOLD_SOFT = 'var(--pvta-gold-soft)';
export const GOLD_MUTED = 'var(--pvta-gold-muted)';

/** Standard-Card: weiß, Radius md, dezente Border + weicher Schatten. */
export const cardSx: Sx = {
    borderRadius: 'var(--pvta-radius-md)',
    border: '1px solid var(--pvta-border)',
    boxShadow: 'var(--pvta-shadow-card)',
    backgroundColor: 'var(--pvta-surface)',
};

/** Prominente Card mit größerem Radius (lg) – für Section-/Hero-Flächen. */
export const cardLgSx: Sx = {
    borderRadius: 'var(--pvta-radius-lg)',
    border: '1px solid var(--pvta-border)',
    boxShadow: 'var(--pvta-shadow-card)',
    backgroundColor: 'var(--pvta-surface)',
};

/** Hover-Anhebung für klickbare Cards (Desktop). */
export const cardHoverSx: Sx = {
    transition: 'box-shadow 160ms ease, transform 160ms ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 'var(--pvta-shadow-hover)',
    },
};

/**
 * Basis für Accordion-/Section-Cards. Die innere Polsterung übernimmt
 * die SectionCard-Komponente, damit Summary/Details konsistent sitzen.
 */
export const sectionCardSx: Sx = {
    borderRadius: 'var(--pvta-radius-lg)',
    border: '1px solid var(--pvta-border)',
    boxShadow: 'var(--pvta-shadow-card)',
    backgroundColor: 'var(--pvta-surface)',
    overflow: 'hidden',
    '&:before': { display: 'none' },
};

/** Größere, ruhigere Inputs mit dunkelgrünem Focus-State. */
export const fieldSx: Sx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 'var(--pvta-radius-sm)',
        backgroundColor: 'var(--pvta-surface)',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(20, 35, 25, 0.14)' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: GREEN_700 },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: GREEN_800,
        borderWidth: 2,
    },
};

/** Goldener Icon-Kreis für Section-Header. Standardgröße 40px. */
export const makeIconCircleSx = (size = 40): Sx => ({
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--pvta-gold-soft)',
    color: GREEN_800,
    flexShrink: 0,
});

export const iconCircleSx: Sx = makeIconCircleSx();

/** Primärer Button: dunkelgrüne Pill, weiße Schrift, ≥44px Touch-Ziel. */
export const primaryButtonSx: Sx = {
    backgroundColor: GREEN_800,
    color: '#ffffff',
    borderRadius: 'var(--pvta-radius-pill)',
    fontWeight: 700,
    minHeight: 44,
    px: 2.5,
    boxShadow: '0 6px 16px rgba(18, 61, 36, 0.22)',
    '&:hover': { backgroundColor: GREEN_900, boxShadow: '0 8px 20px rgba(18, 61, 36, 0.28)' },
};

/** Sekundärer Button: weiß/transparent mit dunkelgrünem Text + Border. */
export const secondaryButtonSx: Sx = {
    backgroundColor: 'var(--pvta-surface)',
    color: GREEN_800,
    borderRadius: 'var(--pvta-radius-pill)',
    fontWeight: 700,
    minHeight: 44,
    px: 2.5,
    border: '1px solid var(--pvta-border-strong)',
    boxShadow: 'none',
    '&:hover': { backgroundColor: 'var(--pvta-surface-muted)', borderColor: GREEN_700 },
};

/** Danger-Button: dezent rot, nicht grell. */
export const dangerButtonSx: Sx = {
    backgroundColor: 'var(--pvta-danger-bg)',
    color: 'var(--pvta-danger-text)',
    borderRadius: 'var(--pvta-radius-pill)',
    fontWeight: 700,
    minHeight: 44,
    px: 2.5,
    border: '1px solid rgba(180, 35, 24, 0.28)',
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#fbdcd9' },
};

/** Semantische Badge-Töne für StatusChip (Token-basiert, keine neuen Fachwerte). */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'green' | 'gold';

export const STATUS_TONES: Record<StatusTone, { bg: string; color: string; border: string }> = {
    success: { bg: 'var(--pvta-success-bg)', color: 'var(--pvta-success-text)', border: 'rgba(31, 107, 52, 0.25)' },
    warning: { bg: 'var(--pvta-warning-bg)', color: 'var(--pvta-warning-text)', border: 'rgba(154, 106, 0, 0.28)' },
    danger: { bg: 'var(--pvta-danger-bg)', color: 'var(--pvta-danger-text)', border: 'rgba(180, 35, 24, 0.28)' },
    info: { bg: 'var(--pvta-info-bg)', color: 'var(--pvta-info-text)', border: 'rgba(20, 96, 107, 0.25)' },
    neutral: { bg: 'var(--pvta-surface-muted)', color: 'var(--pvta-muted)', border: 'var(--pvta-border-strong)' },
    green: { bg: 'var(--pvta-green-100)', color: GREEN_700, border: 'rgba(31, 77, 43, 0.25)' },
    gold: { bg: 'var(--pvta-gold-soft)', color: GOLD_MUTED, border: 'rgba(200, 169, 77, 0.55)' },
};
